import {Dropdown, Tooltip} from 'platform/components';
import {Center, Icon} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {useSelector} from 'react-redux';

import {isNil} from 'ramda';

import {selectCurrentUserInfo, useSetUserAwayMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {useFocusControl} from '@dms/shared';

import {useBoolean} from 'shared/web';

import {SET_USER_AWAY_MUTATION_KEY} from '../constants/SET_USER_AWAY_MUTATION_KEY';
import {AwayStatusMenu} from './AwayStatusMenu';
import {HeaderButtonWrapper} from './HeaderButtonWrapper';
import {TopBarActionContainer} from './TopBarActionContainer';

export function AwayStatusSelect() {
  const [isDropdownOpen, openDropdown, closeDropdown] = useBoolean();
  const {isDisabled} = useFocusControl();

  const {data: userInfo} = useSelector(selectCurrentUserInfo);
  const [, {isLoading}] = useSetUserAwayMutation({fixedCacheKey: SET_USER_AWAY_MUTATION_KEY});

  if (isNil(userInfo)) {
    return null;
  }

  const isAway = userInfo.isAway ?? false;

  const statusLabel = match<boolean, string>(isAway)
    .with(true, () => i18n.t('user.awayStatus.away'))
    .with(false, () => i18n.t('user.awayStatus.available'))
    .exhaustive();

  const arrowIcon = match<boolean, 'navigation/arrow_drop_up' | 'navigation/arrow_drop_down'>(
    isDropdownOpen
  )
    .with(true, () => 'navigation/arrow_drop_up' as const)
    .with(false, () => 'navigation/arrow_drop_down' as const)
    .exhaustive();

  return (
    <Dropdown
      data-testid="awayStatusSelect-dropdown"
      closeOnSelect
      isOpen={isDropdownOpen}
      onOpen={openDropdown}
      onClose={closeDropdown}
      dropdownControl={
        <TopBarActionContainer>
          <Center height={12}>
            <HeaderButtonWrapper $isOpen={isDropdownOpen}>
              <Tooltip label={i18n.t('user.awayStatus.tooltip')} isDisabled={!isAway}>
                <StyledTrigger
                  aria-label="topBar awayStatus"
                  tabIndex={isDisabled ? -1 : 0}
                  disabled={isLoading}
                  data-testid="awayStatusSelect"
                >
                  <StatusDot $isAway={isAway} />
                  {statusLabel}
                  <Icon value={arrowIcon} size={4} />
                </StyledTrigger>
              </Tooltip>
            </HeaderButtonWrapper>
          </Center>
        </TopBarActionContainer>
      }
      placement="bottom-start"
      isLazy
      lazyBehavior="unmount"
    >
      <AwayStatusMenu />
    </Dropdown>
  );
}

interface StatusDotProps {
  $isAway: boolean;
}

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const StatusDot = styled.div<StatusDotProps>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({theme, $isAway}) =>
    $isAway ? theme.colors.palettes.yellow[60][100] : theme.colors.palettes.green[60][100]};
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const StyledTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  background: transparent;
  border: none;
  cursor: pointer;
  height: ${({theme}) => theme.getSize(8)};
  padding-block: 6px;
  padding-inline: ${({theme}) => theme.getSize(3)};
  border-radius: ${({theme}) => theme.getSize(1)};
  font-size: ${({theme}) => theme.fontSizes.text.small};
  font-weight: ${({theme}) => theme.fontWeights.regular};
  line-height: ${({theme}) => theme.lineHeights.text.small};
  white-space: nowrap;
  transition: all 250ms;
  color: ${({theme}) => theme.colors.text.primary};

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
